import { useState, useEffect } from "react";
import { useUser } from "@/hooks/use-user";
import { useLocation } from "wouter";
import Layout from "@/components/layout";
import useSWR, { mutate } from "swr";
import { fetchRoles, fetchVendors } from "@/lib/api";
import type { Project, Candidate } from "db/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent, DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Plus, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { supabase } from "../../supabaseClient";
import RoleForm from "@/components/role-form";

export default function Roles() {
  const { user, isLoading: userLoading } = useUser();
  const [, setLocation] = useLocation();
  const { data: roles, error: rolesError, isLoading: rolesLoading } = useSWR('projects', fetchRoles);
  const { data: vendors } = useSWR('vendors', fetchVendors);
  const [open, setOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Project | null>(null);
  const [roleDetails, setRoleDetails] = useState<Project | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [eligibleCandidates, setEligibleCandidates] = useState<Candidate[] | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activetab, setActiveTab] = useState('open');
  const handleCandidateClick = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
  };

  const statuses = ['Open','In Progress','All','On Hold','Successfully Placed','Closed (Unfilled)','Offer Made']

  useEffect(() => {
    if (!userLoading && !user) {
      setLocation("/login");
    }
  }, [user, userLoading, setLocation]);

  useEffect(() => {
    console.log('Roles data:', roles);
    console.log('Vendors data:', vendors);
  }, [roles, vendors]);

  if (rolesError) {
    console.error('Error loading roles:', rolesError.message);
    return <div>Error loading roles: {rolesError.message}</div>;
  }

  if (rolesLoading || userLoading) {
    return <Layout><div>Loading...</div></Layout>;
  }

  if (!user) return null;

  const handleSuccess = () => {
    setOpen(false);
    setEditingRole(null);
    mutate('roles');
  };

  const getVendorName = (vendorId: number) => {
    return vendors?.find((v) => v.id === vendorId)?.name || "Unknown Vendor";
  };

  const fetchRoleDetails = async (id: number) => {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;

      setRoleDetails(data);

      await fetchEligibleCandidates(data.skills_required);
    } catch (error) {
      console.error("Error fetching role details:", error.message);
    }
  };

  const fetchEligibleCandidates = async (skillsRequired: string | string[]) => {
    try {
      const skillsArray = Array.isArray(skillsRequired)
        ? skillsRequired
        : skillsRequired.split(",").map((skill) => skill.trim());

      const { data, error } = await supabase
        .from("candidates")
        .select("*")
        .contains("skills", skillsArray);

      if (error) throw error;
      setEligibleCandidates(data);
    } catch (error) {
      console.error("Error fetching candidates:", error.message);
    }
  };

  const handleRoleClick = async (roleId: number) => {
    // Navigate to the role details page
    setLocation(`/role-details/${roleId}`);
  };

  // Filter roles based on search query
  const filteredRoles = roles?.filter((role) =>
    activetab === 'all' ? role.title.toLowerCase().includes(searchQuery.toLowerCase()) : (role.title.toLowerCase().includes(searchQuery.toLowerCase()) && role.status.toLowerCase().trim()===activetab)
  );

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Roles</h2>
            <p className="text-muted-foreground">
              Manage your ongoing roles
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Role
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogTitle>Add role</DialogTitle>
              <RoleForm
                onSuccess={handleSuccess}
                initialData={editingRole}
                vendors={vendors || []}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Search Bar */}
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>
        {/* tabs */}
        <div className="flex gap-10 text-m font-semibold">
          {statuses?.map((status,index)=>
          (
            <a href="#" key={index}
            className={`${activetab === status.toLowerCase() ? 'border-b-2 border-black text-black' : 'text-gray-500'}`}
            onClick={() => setActiveTab(status.toLowerCase())}>
            {status}</a>
          ))}
         
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredRoles?.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              onClick={() => handleRoleClick(role.id)} // Navigate on click
            />
          ))}
        </div>
      </div>
    </Layout>
  );
}

function RoleCard({
  role,
  onClick,
}: {
  role: Role;
  onClick: () => void;
}) {
  return (
    <Card
      key={role.id}
      className="relative cursor-pointer hover:shadow-lg"
      onClick={onClick} // Trigger navigation
    >
      <CardContent className="pt-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-extrabold text-gray-900">{role.title}</h3>
            <Badge
              variant={role.status === "active" ? "default" : "secondary"}
            >
              {role.status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{role.description}</p>
          <div className="space-y-1 text-sm">
            {role.vendor && (
              <div className="text-muted-foreground">
                Vendor: {role.vendor.name}
              </div>
            )}
            {role.job_posted_date && (
              <div className="flex items-center text-muted-foreground">
                <Calendar className="mr-2 h-4 w-4" />
                {format(new Date(role.job_posted_date), "MMM d, yyyy")}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
